import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import { MapContainer, TileLayer, useMap, useMapEvent } from 'react-leaflet'
import '../../utils/Leaflet/leaflet-distance-marker'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-gpx'
import Box from '@mui/material/Box'
import { AntPath } from 'leaflet-ant-path'
import Container from '@mui/material/Container'
import CustomMarker from './Marker'
import './DistanceMarkerStyles.css'
import { useTranslation } from 'react-i18next'

// Fix leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
})

const AntPathLayer = ({ gpxData, showDistanceMarkers, distanceInterval, locations }) => {
  const { t } = useTranslation('components')
  const map = useMap()

  useEffect(() => {
    if (gpxData && map) {
      const parser = new DOMParser()
      const gpxDoc = parser.parseFromString(gpxData, 'text/xml')

      // Extract coordinates and elevation from GPX
      const trkpts = gpxDoc.querySelectorAll('trkpt')
      const latlngs = Array.from(trkpts).map(pt => {
        return [
          parseFloat(pt.getAttribute('lat')),
          parseFloat(pt.getAttribute('lon'))
        ]
      })

      // Create start and finish markers
      const startIcon = new L.Icon({
        iconUrl: 'https://d1hi0tbu2kom2c.cloudfront.net/assets/start.png',
        iconRetinaUrl: 'https://d1hi0tbu2kom2c.cloudfront.net/assets/start.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
        iconSize: [33, 45],
        iconAnchor: [16, 45],
        popupAnchor: [0, -45]
      })

      const endIcon = new L.Icon({
        iconUrl: 'https://d1hi0tbu2kom2c.cloudfront.net/assets/finish.png',
        iconRetinaUrl: 'https://d1hi0tbu2kom2c.cloudfront.net/assets/finish.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
        iconSize: [33, 45],
        iconAnchor: [0, 45],
        popupAnchor: [0, -45]
      })

      if (latlngs.length > 0) {
        // Find start and finish location data if available
        const startLocation = locations?.find(loc =>
          loc.cp && (loc.cp.toLowerCase() === 'start' || loc.cp.toLowerCase() === 'start'))
        const finishLocation = locations?.find(loc =>
          loc.cp && (loc.cp.toLowerCase() === 'finish' || loc.cp.toLowerCase() === 'finish'))

        // Add start marker with location data popup
        const startMarker = L.marker(latlngs[0], { icon: startIcon }).addTo(map)
        if (startLocation) {
          // Create popup content from location data
          const popupContent = createLocationPopupContent(startLocation, t)
          startMarker.bindPopup(popupContent)
        } else {
          startMarker.bindPopup(t('map.start'))
        }

        // Add finish marker with location data popup
        const endMarker = L.marker(latlngs[latlngs.length - 1], { icon: endIcon }).addTo(map)
        if (finishLocation) {
          // Create popup content from location data
          const popupContent = createLocationPopupContent(finishLocation, t)
          endMarker.bindPopup(popupContent)
        } else {
          endMarker.bindPopup(t('map.finish'))
        }

        // Create and add AntPath with distance markers enabled
        const antPath = new AntPath(latlngs, {
          delay: 5000,
          dashArray: [20, 6],
          weight: 3,
          color: '#333',
          pulseColor: 'orangered',
          paused: false,
          reverse: false,
          hardwareAccelerated: true,
          opacity: 0.9
        })
        antPath.addTo(map)
        antPath.bringToFront()

        // Create a regular polyline for distance markers (hidden)
        let distancePolyline
        if (showDistanceMarkers) {
          distancePolyline = L.polyline(latlngs, {
            distanceMarkers: {
              offset: distanceInterval * 1000 || 0, // Show markers every X meters
              showAll: 12,  // Show all markers at zoom level 12 and above
              cssClass: 'dist-marker',
              iconSize: [24, 24]
            },
            opacity: 0, // Make the polyline itself invisible
            weight: 0
          }).addTo(map)
        }

        // Fit map bounds to the route
        const bounds = L.latLngBounds(latlngs)
        map.fitBounds(bounds)

        return () => {
          if (antPath) map.removeLayer(antPath)
          if (distancePolyline) map.removeLayer(distancePolyline)
          if (startMarker) map.removeLayer(startMarker)
          if (endMarker) map.removeLayer(endMarker)
        }
      }
    }
  }, [gpxData, map, showDistanceMarkers, distanceInterval, locations])

  return null
}

// Helper function to create popup content from location data
function createLocationPopupContent(location, t) {
  if (!location) return ''

  // Format the label
  const formattedLabel = location.cp ?
    (location.cp.toLowerCase() === 'start' || location.cp.toLowerCase() === 'finish') ?
      location.cp :
      `CP${location.cp}: ${location.name}` :
    location.name || ''

  // Build popup content
  let content = '<div style="max-width: 200px;">'
    + `<h3 style="margin: '8px 0'; color: '#333';">${formattedLabel}</h3>`
    + '<div style="font-size: \'14px\'; margin-bottom: \'8px\';">'
    + `${location.interDistKm ? `<div><strong>${t('map.labels.interDist')}</strong> ${location.interDistKm} km</div>` : ''}`
    + `${location.commDistKm ? `<div><strong>${t('map.labels.commDist')}</strong> ${location.commDistKm} km</div>` : ''}`
    + `${location.commDPlusM ? `<div><strong>${t('map.labels.dPlus')}</strong> ${location.commDPlusM} m</div>` : ''}`
    + `${location.commDMinusM ? `<div><strong>${t('map.labels.dMinus')}</strong> ${location.commDMinusM} m</div>` : ''}`
    + `${location.altitudeM ? `<div><strong>${t('map.labels.altitude')}</strong> ${location.altitudeM} m</div>` : ''}`
    + `${location.crewSupport ? `<div><strong>${t('map.labels.crewSupport')}</strong> ${location.crewSupport}</div>` : ''}`
    + `${location.services ? `<div><strong>${t('map.labels.services')}</strong> ${location.services}</div>` : ''}`
    + `${location.location ? `<div><strong>${t('map.labels.location')}</strong> ${location.location}</div>` : ''}`
    + `${location.cutOffTime ? `<div><strong>${t('map.labels.cutOffTime')}</strong> ${location.cutOffTime}</div>` : ''}`
    + '</div>'
    + '</div>'

  return content
}

const MapViewerWithRef = forwardRef(({ gpxFilePath, clickable = true, showDistanceMarkers, distanceInterval, locations, showNames, userLocation }, ref) => {
  const { t } = useTranslation('components')
  const animateRef = useRef(true)
  const [gpxData, setGpxData] = useState(null)
  const waypoints = []
  const mapRef = useRef(null)

  // Track markers at the same location for tooltip offsetting and popup content
  const tooltipPositionMap = useRef(new Map())
  const labelPositionMap = useRef(new Map())

  // Function to get tooltip offset for a given position
  const getTooltipOffset = (lat, lng) => {
    const positionKey = `${lat.toFixed(6)},${lng.toFixed(6)}`

    if (!tooltipPositionMap.current.has(positionKey)) {
      tooltipPositionMap.current.set(positionKey, 0)
      return [0, -25] // First marker at this position
    }

    const count = tooltipPositionMap.current.get(positionKey) + 1
    tooltipPositionMap.current.set(positionKey, count)

    // Calculate vertical offset: -25px for first, -50px for second, etc.
    return [0, -25 - (count * 25)]
  }

  // Function to get concatenated CP values for overlapping markers
  const getConcatenatedCPLabel = (lat, lng, location) => {
    const positionKey = `${lat.toFixed(6)},${lng.toFixed(6)}`

    // For SAMAR TRAIL locations, add specific logging
    // if (location.name === 'SAMAR TRAIL') {
    //   console.log(`SAMAR TRAIL marker at position ${positionKey}:`, location)
    // }

    // Initialize position data if it doesn't exist
    if (!labelPositionMap.current.has(positionKey)) {
      labelPositionMap.current.set(positionKey, {
        count: 0,
        locations: []
      })
    }

    const positionData = labelPositionMap.current.get(positionKey)
    positionData.locations.push(location)
    positionData.count++

    // If this is the first marker at this position, return null (no concatenation needed)
    if (positionData.count === 1) {
      return null
    }

    // For multiple markers, concatenate CP values
    const cpValues = positionData.locations
      .map(loc => loc.cp ? `CP${loc.cp}` : loc.name)
      .filter(Boolean)

    const uniqueCPValues = [...new Set(cpValues)] // Remove duplicates
    const name = positionData.locations[0].name || ''

    return `${uniqueCPValues.join(', ')}: ${name}`
  }

  // Reset position maps when locations change
  useEffect(() => {
    tooltipPositionMap.current = new Map()
    labelPositionMap.current = new Map()
  }, [locations])

  // Expose map methods to parent component
  useImperativeHandle(ref, () => ({
    setView: (latlng, zoom) => {
      if (mapRef.current) {
        mapRef.current.setView(latlng, zoom)
      }
    }
  }))

  // Filter out start and finish locations since they're handled by GPX
  const filteredLocations = locations?.filter(location => {
    const cp = location.cp?.toLowerCase()
    return cp !== 'start' && cp !== 'finish'
  }) || []

  const SetViewOnClick = ({ animateRef }) => {
    const map = useMapEvent('click', (e) => {
      // this.setState({ maxZoom: null })
      if (clickable && e.latlng) {
        // const lat = e.latlng?.lat && parseFloat(e.latlng.lat.toFixed(7))
        // const lng = e.latlng?.lng && parseFloat(e.latlng.lng.toFixed(7))
        // setLocation({ lat, lng })
        // getLocationInTopState({ lat, lng })

        // Getting the location on click here
        // console.log({ lat, lng })
      }

      map.setView(e.latlng, map.getZoom(), {
        animate: animateRef.current || false
      })
    })
    return null
  }

  useEffect(() => {
    fetch(gpxFilePath)
      .then(res => res.text())
      .then(data => {
        const parser = new DOMParser()
        parser.parseFromString(data, 'text/xml')
        setGpxData(data)
      })
      .catch(err => console.error('Error loading GPX:', err))
  }, [])

  return (
    <Container disableGutters maxWidth={false}>
      <Box sx={{ height: { xs: '100vh', sm: '92vh' }, width: '100vw' }}>
        <MapContainer
          ref={mapRef}
          style={{
            height: '99%',
            width: '100%',
            margin: 0,
            padding: 0
          }}
          attributionControl={true}
        >
          <TileLayer
            url='https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
            attribution='&copy; Powered by <a href="https://www.esri.com/">Esri</a>, Maxar, Earthstar Geographics, and the GIS User Community'
          />
          {gpxData && <AntPathLayer {...{ gpxData, showDistanceMarkers, distanceInterval, locations }} />}
          {waypoints.map((wp, i) => (
            <CustomMarker
              key={i}
              position={[wp.lat, wp.lng]}
              label={wp.label}
              preview={wp.preview}
              description={wp.description}
            />
          ))}
          {filteredLocations && filteredLocations.map((location, index) => {
            const key = `location-${index}`
            // console.log(`Props for marker with key '${key}':`, { location, showName: showNames, index })
            // Calculate tooltip offset for this marker
            const tooltipOffset = getTooltipOffset(
              parseFloat(location.gpxLocation.lat),
              parseFloat(location.gpxLocation.lng)
            )

            // Get concatenated label for popup if multiple markers at same position
            const concatenatedLabel = getConcatenatedCPLabel(
              parseFloat(location.gpxLocation.lat),
              parseFloat(location.gpxLocation.lng),
              location
            )

            return (<CustomMarker
              key={key}
              location={location}
              showName={showNames}
              index={index}
              tooltipOffset={tooltipOffset}
              concatenatedLabel={concatenatedLabel}
                    />)
          })}
          {userLocation && (
            <CustomMarker
              key='user-location'
              position={[userLocation.lat, userLocation.lng]}
              label={t('map.yourLocation')}
              iconUrl='https://d1hi0tbu2kom2c.cloudfront.net/assets/me.png'
              isUserLocation={true}
            />
          )}
          {/* {console.log('User Location:', userLocation)} */}
          <SetViewOnClick animateRef={animateRef} />
        </MapContainer>
      </Box>
    </Container>
  )
})

// Export with display name for debugging
MapViewerWithRef.displayName = 'MapViewer'
export default MapViewerWithRef
